import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, Crown, Flame } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { AdBanner, AdSidebar } from "@/components/AdBanner";
import { PlayGate } from "@/components/PlayGate";
import { gameById } from "@/data/games";
import { dailyGame, todayStr } from "@/lib/daily";
import { usePlayGate } from "@/hooks/usePlayGate";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

import { SnakeGame } from "@/games/SnakeGame";
import { Game2048 } from "@/games/Game2048";
import { MemoryMatch } from "@/games/MemoryMatch";
import { SimonGame } from "@/games/SimonGame";
import { MathBlitz } from "@/games/MathBlitz";
import { ReactionDuel } from "@/games/ReactionDuel";
import { IQTest } from "@/games/IQTest";
import { WhackGame } from "@/games/WhackGame";
import { WordGuess } from "@/games/WordGuess";
import { TicTacToe } from "@/games/TicTacToe";
import { ConnectFour } from "@/games/ConnectFour";
import { SlidingPuzzle } from "@/games/SlidingPuzzle";
import { TypingTest } from "@/games/TypingTest";
import { ReactionTime } from "@/games/ReactionTime";
import { BrickBreaker } from "@/games/BrickBreaker";
import { FlappyBlock } from "@/games/FlappyBlock";
import { DinoRun } from "@/games/DinoRun";
import { BubblePop } from "@/games/BubblePop";
import { Minesweeper } from "@/games/Minesweeper";
import { LightsOut } from "@/games/LightsOut";
import { DigitMemory } from "@/games/DigitMemory";
import { StroopTest } from "@/games/StroopTest";
import { SchulteTable } from "@/games/SchulteTable";
import { TapWar } from "@/games/TapWar";
import { AimTrainer } from "@/games/AimTrainer";
import { RockPaperScissors } from "@/games/RockPaperScissors";
import { Hangman } from "@/games/Hangman";
import { TowerStack } from "@/games/TowerStack";
import { PianoTiles } from "@/games/PianoTiles";
import { MemoryGrid } from "@/games/MemoryGrid";
import { ClickSpeed } from "@/games/ClickSpeed";
import { ReflexGrid } from "@/games/ReflexGrid";
import { NumberGuess } from "@/games/NumberGuess";
import { CoinStreak } from "@/games/CoinStreak";
import { BalloonRisk } from "@/games/BalloonRisk";
import { WordScramble } from "@/games/WordScramble";
import { DiceDuel } from "@/games/DiceDuel";
import { SpacebarMasher } from "@/games/SpacebarMasher";
import { TimeGuesser } from "@/games/TimeGuesser";
import { Bullseye } from "@/games/Bullseye";
import { EggCatch } from "@/games/EggCatch";
import { MeteorDodge } from "@/games/MeteorDodge";
import { SortNumbers } from "@/games/SortNumbers";
import { HanoiGame } from "@/games/HanoiGame";
import { SpeedChoice, SPEED_CHOICE_IDS } from "@/games/SpeedChoice";

const REGISTRY = {
  snake: SnakeGame,
  "2048": Game2048,
  memory: MemoryMatch,
  simon: SimonGame,
  "math-blitz": MathBlitz,
  "reaction-duel": ReactionDuel,
  "iq-test": IQTest,
  whack: WhackGame,
  "word-mania": WordGuess,
  tictactoe: TicTacToe,
  "connect-four": ConnectFour,
  "sliding-puzzle": SlidingPuzzle,
  "typing-test": TypingTest,
  "reaction-time": ReactionTime,
  "brick-breaker": BrickBreaker,
  flappy: FlappyBlock,
  "dino-run": DinoRun,
  "bubble-pop": BubblePop,
  minesweeper: Minesweeper,
  "lights-out": LightsOut,
  "digit-memory": DigitMemory,
  stroop: StroopTest,
  schulte: SchulteTable,
  "tap-war": TapWar,
  "aim-trainer": AimTrainer,
  rps: RockPaperScissors,
  hangman: Hangman,
  "tower-stack": TowerStack,
  "piano-tiles": PianoTiles,
  "memory-grid": MemoryGrid,
  "click-speed": ClickSpeed,
  "reflex-grid": ReflexGrid,
  "number-guess": NumberGuess,
  "coin-streak": CoinStreak,
  "balloon-risk": BalloonRisk,
  "word-scramble": WordScramble,
  "dice-duel": DiceDuel,
  "spacebar-masher": SpacebarMasher,
  "time-guesser": TimeGuesser,
  bullseye: Bullseye,
  "egg-catch": EggCatch,
  "meteor-dodge": MeteorDodge,
  "sort-numbers": SortNumbers,
  hanoi: HanoiGame,
};

export default function PlayPage() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const game = gameById(gameId);
  const isSpeed = SPEED_CHOICE_IDS.includes(gameId);
  const GameComp = isSpeed ? SpeedChoice : REGISTRY[gameId];
  const { user, checkAuth } = useAuth();
  const { blocked, playsLeft, registerPlay, isAuthed } = usePlayGate();
  const isDaily = dailyGame().id === gameId;
  const [gateBlocked, setGateBlocked] = useState(false);
  const [bestScore, setBestScore] = useState(null);
  const registered = useRef(false);

  useEffect(() => {
    if (!game) return;
    if (registered.current) return;
    registered.current = true;
    registerPlay().then((ok) => { if (!ok) setGateBlocked(true); });
  }, [game, registerPlay]);

  useEffect(() => { setGateBlocked(blocked); }, [blocked]);

  const handleScore = async (score) => {
    if (game.type === "versus" || game.type === "quiz") return;
    if (!user) {
      toast.info("Log in to save your score to the leaderboard!");
      return;
    }
    if (score > 0) {
      try {
        await api.post("/scores", { game_id: game.id, game_title: game.title, score });
        toast.success(`Score saved: ${score}!`);
        setBestScore(score);
      } catch {
        toast.error("Couldn't save score. Try again.");
      }
    }
    if (isDaily) {
      try {
        const { data } = await api.post("/daily/complete", { date: todayStr() });
        if (!data.already) {
          toast.success(`🔥 Daily streak: ${data.streak} ${data.streak === 1 ? "day" : "days"}!`, { duration: 5000 });
        }
        checkAuth();
      } catch {}
    }
  };

  if (!game) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="font-display font-black text-3xl uppercase">Game not found</p>
        <Link to="/arcade" className="text-neon-cyan hover:underline mt-4 inline-block">← Back to games</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8" data-testid="play-page">
      <Seo title={`Play ${game.title}`} description={`Play ${game.title} free online. ${game.blurb}`} />

      <Link to={game.category === "quiz" ? "/quizzes" : game.category === "brain" ? "/brain-teasers" : "/arcade"}
        className="inline-flex items-center gap-1 text-zinc-400 hover:text-neon-cyan text-sm font-semibold uppercase tracking-widest mb-6">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter">{game.title}</h1>
                {isDaily && (
                  <span className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-neon-lime text-black rounded" data-testid="daily-badge">
                    <Flame className="w-3 h-3" /> Daily Challenge
                  </span>
                )}
              </div>
              <p className="text-zinc-400">{game.blurb}</p>
            </div>
            {!isAuthed && playsLeft !== Infinity && (
              <span className="font-mono text-xs px-3 py-1.5 rounded-full border border-neon-yellow text-neon-yellow" data-testid="plays-left">
                {playsLeft} free {playsLeft === 1 ? "play" : "plays"} left
              </span>
            )}
          </div>

          <div className="bg-zinc-950 border-2 border-zinc-800 rounded-2xl p-6 md:p-10 flex items-center justify-center min-h-[480px]">
            {gateBlocked ? <PlayGate playsLeft={playsLeft} /> : <GameComp onScore={handleScore} {...(isSpeed ? { variant: gameId } : {})} />}
          </div>

          {bestScore != null && (
            <div className="mt-4 flex items-center gap-2 text-neon-lime font-mono">
              <Trophy className="w-4 h-4" /> Last score saved: {bestScore}
            </div>
          )}

          <AdBanner slot="play" className="mt-8" />
        </div>

        <aside className="lg:w-64 shrink-0 space-y-6">
          <div className="bg-zinc-950 border-2 border-zinc-800 rounded-xl p-5">
            <h3 className="font-display font-bold uppercase text-sm mb-3 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-neon-yellow" /> Leaderboard
            </h3>
            <p className="text-sm text-zinc-400 mb-3">See who's topping the charts.</p>
            <Button onClick={() => navigate("/leaderboard")} variant="outline"
              className="w-full border-zinc-700 text-white rounded-none text-xs uppercase tracking-widest hover:border-neon-cyan">
              View Rankings
            </Button>
          </div>
          {!user?.is_premium && (
            <div className="bg-zinc-950 border-2 border-neon-pink/40 rounded-xl p-5">
              <Crown className="w-6 h-6 text-neon-yellow mb-2" />
              <h3 className="font-display font-bold uppercase text-sm mb-1">Go Ad-Free</h3>
              <p className="text-sm text-zinc-400 mb-3">Premium removes all ads.</p>
              <Button onClick={() => navigate("/subscribe")}
                className="w-full bg-neon-pink text-white rounded-none text-xs uppercase tracking-widest hover:bg-white hover:text-black">
                Get Premium
              </Button>
            </div>
          )}
          <AdSidebar />
        </aside>
      </div>
    </div>
  );
}
